import type { AppStore } from "@/store/AppStore";
import {
  AiToolkitCollectionNotFoundError,
  AiToolkitNoteNotFoundError,
} from "@/modules/ai-toolkit/errors";
import type {
  RemoveNoteFromCollectionInput,
  RemoveNoteFromCollectionOutput,
} from "@mem-client/ai-toolkit/contract";
import {
  AiToolkitToolName,
  removeNoteFromCollectionInputSchema,
  removeNoteFromCollectionOutputSchema,
} from "@mem-client/ai-toolkit/contract";
import type { AiToolkitToolDefinition } from "@/modules/ai-toolkit/tools/types";

export const removeNoteFromCollection = async ({
  store,
  input,
}: {
  store: AppStore;
  input: RemoveNoteFromCollectionInput;
}): Promise<RemoveNoteFromCollectionOutput> => {
  const [note, collection] = await Promise.all([
    store.notes.getAsync(input.noteId),
    store.collections.getAsync(input.collectionId),
  ]);

  if (!note?.isAvailable) {
    throw new AiToolkitNoteNotFoundError({ noteId: input.noteId });
  }
  if (!collection) {
    throw new AiToolkitCollectionNotFoundError({ collectionId: input.collectionId });
  }

  const wasInCollection = await collection.itemList.hasItemAsync({ itemId: input.noteId });

  if (wasInCollection) {
    await collection.itemList.removeItem({ itemId: input.noteId });
  }

  return {
    noteId: input.noteId,
    collectionId: input.collectionId,
    removed: wasInCollection,
  };
};

export const removeNoteFromCollectionTool: AiToolkitToolDefinition<
  AiToolkitToolName.RemoveNoteFromCollection,
  RemoveNoteFromCollectionInput,
  RemoveNoteFromCollectionOutput
> = {
  name: AiToolkitToolName.RemoveNoteFromCollection,
  inputSchema: removeNoteFromCollectionInputSchema,
  outputSchema: removeNoteFromCollectionOutputSchema,
  strict: true,
  execute: removeNoteFromCollection,
};
